import React, { useState, memo } from 'react';
import { View, Text, StyleSheet, Platform, Linking, Pressable } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { CommunityResource, ICommunityResourceFile } from '@/types/residents/communityResource';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ResourceIcon } from './ResourceIcon';
import { openExternalUrl } from '@/utils/common/linking';

interface ResourcePanelProps {
  resource: CommunityResource | null;
  open: boolean;
  onClose: () => void;
}

const ResourcePanelBase: React.FC<ResourcePanelProps> = ({ resource, open, onClose }) => {
  const [linkHovered, setLinkHovered] = useState(false);
  const [hoveredFileId, setHoveredFileId] = useState<number | null>(null);

  return (
    <DesktopSidePanel visible={open} onClose={onClose} width={520}>
      {resource && (
        <View style={styles.panelBody}>
          <Pressable onPress={onClose} accessibilityRole="button" style={styles.panelCloseRow}>
            <Icon name="close-outline" size={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} />
          </Pressable>
          <View style={styles.panelIconCircle}>
            <ResourceIcon iconKey={resource.icon || resource.iconName} size={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} />
          </View>
          <Text style={styles.panelTitle}>{resource.name || resource.title}</Text>
          {!!resource.description && <Text style={styles.panelSubtitle}>{resource.description}</Text>}

          <View style={styles.panelSections}>
            {!!resource.content && (
              <View style={styles.infoBanner}>
                <Text style={styles.infoBannerText}>{resource.content}</Text>
              </View>
            )}

            {!!resource.url && (
              <Pressable
                style={[styles.linkRow, linkHovered && styles.linkRowHovered]}
                onHoverIn={() => setLinkHovered(true)}
                onHoverOut={() => setLinkHovered(false)}
                onPress={async () => {
                  const url = resource.url!;
                  await openExternalUrl(url);
                }}
              >
                <Icon name="link-outline" size={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} style={styles.linkIcon} />
                <Text style={styles.linkText} numberOfLines={1}>{resource.url}</Text>
              </Pressable>
            )}

            {(resource.files || []).map((file: ICommunityResourceFile) => (
              <Pressable
                key={file.id}
                style={[styles.fileRow, hoveredFileId === file.id && styles.fileRowHovered]}
                onHoverIn={() => setHoveredFileId(file.id)}
                onHoverOut={() => setHoveredFileId((prev) => (prev === file.id ? null : prev))}
                onPress={async () => {
                  const fileUrl = file.fileUrl;
                  await openExternalUrl(fileUrl);
                }}
              >
                <RESIDENTS_SVG_COMPONENTS.DOCUMENT width={20} height={20} color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} style={styles.linkIcon} />
                <Text style={styles.fileText} numberOfLines={1}>{file.label || `File ${file.id}`}</Text>
              </Pressable>
            ))}
          </View>
        </View>
      )}
    </DesktopSidePanel>
  );
};

ResourcePanelBase.displayName = 'ResourcePanel';

export const ResourcePanel = memo(ResourcePanelBase);

const styles = StyleSheet.create({
  panelBody: {
    flex: 1,
    padding: 24,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  panelCloseRow: {
    alignSelf: 'flex-end',
    marginBottom: 16,
  },
  panelIconCircle: {
    borderRadius: 9999,
    backgroundColor: '#1A1A1C',
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  panelTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 20,
    fontWeight: '600',
    marginTop: 2,
  },
  panelSubtitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 16,
    marginTop: 4,
  },
  panelSections: {
    marginTop: 16,
    gap: 12,
  },
  infoBanner: {
    borderLeftWidth: 4,
    borderLeftColor: '#FCA5A5',
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    borderRadius: 12,
    padding: 12,
  },
  infoBannerText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 14,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    padding: 12,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  linkRowHovered: {
    borderColor: '#3B82F6',
  },
  linkIcon: {
    marginRight: 12,
  },
  linkText: {
    color: '#F3F4F6',
    fontSize: 14,
    flexShrink: 1,
  },
  fileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    padding: 12,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  fileRowHovered: {
    borderColor: '#3B82F6',
  },
  fileText: {
    color: '#F3F4F6',
    fontSize: 14,
    flexShrink: 1,
  },
});


